import type { CVListItemDto } from '@cibli/shared';

import { differenceInCalendarDays } from '@cibli/shared';
import { FileUser } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useCVListContext } from '../contexts/CVListContext';

function formatRelativeDate(dateString: string, t: ReturnType<typeof useTranslation>['t']): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = differenceInCalendarDays(now, date);

  if (diffDays === 0) return t('dashboard.cvList.card.today');
  if (diffDays === 1) return t('dashboard.cvList.card.yesterday');
  if (diffDays < 7) return t('dashboard.cvList.card.daysAgo', { days: diffDays });
  if (diffDays < 14) return t('dashboard.cvList.card.oneWeekAgo');
  if (diffDays < 30)
    return t('dashboard.cvList.card.weeksAgo', { weeks: Math.floor(diffDays / 7) });
  return t('dashboard.cvList.card.monthsAgo', { months: Math.floor(diffDays / 30) });
}

interface CVMiniatureProps {
  cv: CVListItemDto;
}

export default function CVMiniature({ cv }: CVMiniatureProps) {
  const { t } = useTranslation();
  const { onEdit, onDownload, onDelete, deletingId, downloadingId, isDownloading } =
    useCVListContext();

  const isDeleting = deletingId === cv.id;
  const isCvDownloading = downloadingId === cv.id && isDownloading;

  return (
    <div className="pro-card p-4 flex flex-col min-h-[130px]">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-cj-green-light/50 flex items-center justify-center flex-shrink-0">
          <FileUser className="w-4.5 h-4.5 text-cj-green" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm text-cj-green-dark truncate">{cv.jobTitle}</h4>
          <p className="text-[11px] text-cj-green-dark/40 mt-0.5">
            {formatRelativeDate(cv.updatedAt, t)}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center gap-2 mt-auto pt-3 border-t border-cj-green-dark/5">
        <button
          type="button"
          disabled={isDeleting || isCvDownloading}
          onClick={() => onEdit(cv.id)}
          className="text-xs font-semibold text-cj-green-nuance hover:text-cj-green transition"
        >
          {t('dashboard.cvList.card.actions.viewEdit')}
        </button>
        <span className="text-cj-green-dark/20">·</span>
        <button
          type="button"
          disabled={isDeleting || isCvDownloading}
          onClick={() => onDownload(cv)}
          className="text-xs font-semibold text-cj-green-dark/60 hover:text-cj-green-dark transition"
        >
          {isCvDownloading
            ? t('dashboard.cvList.download.downloading')
            : t('dashboard.cvList.card.actions.download')}
        </button>
        <span className="text-cj-green-dark/20">·</span>
        <button
          type="button"
          disabled={isDeleting}
          onClick={() => onDelete(cv.id)}
          className="text-xs font-semibold text-cj-pink/80 hover:text-cj-pink transition"
        >
          {t('dashboard.cvList.card.actions.delete')}
        </button>
      </div>
    </div>
  );
}
